import React, {useState, useEffect, useRef} from "react";
import {useNavigate} from "react-router-dom";
import DataTable from "../../data-table/data-table";
import Topbar from "../../top-bar/top-bar";
import {instanceMob} from "../../../utils/utils";
import useLocationStore from "../config/use-location-store";
import FormField from "../../forms/form-field";
import debounce from "lodash.debounce";
import "./styles.css";

const AccountsReceivableTable = () => {
    const navigate = useNavigate();
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearch = useRef(null);
    const [sortConfig, setSortConfig] = useState({ accessor: null, direction: null });

    const selectedLocation = useLocationStore((state) => state.selectedLocation);

    const [pagination, setPagination] = useState({
        count: 0,
        next: null,
        previous: null,
        currentPage: 1,
    });

    const columns = [
        {
            header: "Nombre",
            accessor: "name",
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-self-center",
            },
        },
        {
            header: "Correo",
            accessor: "email",
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-self-center",
            },
        },
        {
            header: "Celular",
            accessor: "phone",
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-self-center",
            },
        },
        {
            header: "Valor de venta",
            accessor: "value",
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-self-center",
            },
            sortable: true,
        },
        {
            header: "Abonos",
            accessor: "payments",
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-self-center",
            },
            sortable: true,
        },
        {
            header: "Deuda",
            accessor: "debt",
            className: {
                header: "justify-content-center align-items-center",
                cell: "text-center align-self-center",
            },
            sortable: true,
        },
    ];

    const handleNext = () => {
        if (pagination.next) {
            setPagination((prev) => ({
                ...prev,
                currentPage: prev.currentPage + 1,
            }));
        }
    };

    const handlePrevious = () => {
        if (pagination.previous && pagination.currentPage > 1) {
            setPagination((prev) => ({
                ...prev,
                currentPage: prev.currentPage - 1,
            }));
        }
    };


    useEffect(() => {
        if (!selectedLocation?.id) return;

        if (debouncedSearch.current) {
            debouncedSearch.current.cancel();
        }

        debouncedSearch.current = debounce(() => {
            setIsLoading(true);

            let ordering;
            if (sortConfig.accessor && sortConfig.direction) {
                const prefix = sortConfig.direction === "desc" ? "-" : "";
                ordering = `${prefix}${sortConfig.accessor}`;
            }

            instanceMob
                .get(`/admin/accounting/receivable/list`, {
                    params: {
                        location_id: selectedLocation.id,
                        page: pagination.currentPage,
                        search: searchTerm,
                        ordering,
                    },
                })
                .then((res) => {
                    const results = res.data.results || res.data;
                    const mapped = results.map((item, index) => ({
                        id: index,
                        name: item.name,
                        email: item.email,
                        phone:
                            item.phone && item.phone.trim() !== ""
                                ? item.phone
                                : "N/A",
                        value: item.value?.toLocaleString(
                            "es-CO",
                            { style: "currency", currency: "COP" }
                        ),
                        payments: item.payments?.toLocaleString("es-CO", {
                            style: "currency",
                            currency: "COP",
                        }),
                        debt: item.debt?.toLocaleString("es-CO", {
                            style: "currency",
                            currency: "COP",
                        }),
                    }));

                    setTableData(mapped);
                    setPagination((prev) => ({
                        ...prev,
                        count: res.data.count ?? mapped.length,
                        next: res.data.next,
                        previous: res.data.previous,
                    }));
                })
                .catch((err) => {
                    console.error("Error loading accounts receivable:", err);
                })
                .finally(() => setIsLoading(false));
        }, 500);

        debouncedSearch.current();

        return () => {
            if (debouncedSearch.current) debouncedSearch.current.cancel();
        };
    }, [selectedLocation?.id, pagination.currentPage, searchTerm, sortConfig]);


    const handleDownloadExcel = async () => {
        if (!selectedLocation?.id) return;

        setIsDownloading(true);
        try {
            const response = await instanceMob.get(
                `/admin/accounting/receivable/excel`,
                {
                    params: {location_id: selectedLocation.id},
                    responseType: "blob",
                }
            );

            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `accounts_receivable_location_${selectedLocation.id}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error al descargar Excel:", error);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="w-100">
            <Topbar/>
            <div className="incomes-expenses-page w-100 p-3 px-3 gap-4 d-flex flex-column">

                <div className="d-flex flex-row justify-content-between align-items-center position-relative">
                    <div className="d-flex flex-row gap-4 align-items-center">
                        <div className="d-flex flex-row gap-1 justify-content-center align-items-center">
                            <button
                                type="button"
                                className="btn close-button p-1"
                                onClick={() => navigate(-1)}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="36"
                                    height="36"
                                    viewBox="0 0 36 36"
                                    fill="none"
                                >
                                    <path
                                        d="M25.5811 34.4648H9.88379C4.97749 34.4648 1 30.4874 1 25.5811V9.88379C1 4.97749 4.97749 1 9.88379 1H25.5811C30.4874 1 34.4648 4.97749 34.4648 9.88379V25.5811L34.4531 26.0381C34.223 30.5807 30.5807 34.223 26.0381 34.4531L25.5811 34.4648Z"
                                        stroke="#9DACBD"
                                        strokeWidth="2"
                                    />
                                    <path
                                        d="M11.0018 17.6351C11.0018 17.0388 11.2292 16.4425 11.6842 15.9875L20.7139 6.95782C21.6238 6.04792 23.0991 6.04792 24.009 6.95782C24.9189 7.86772 24.9189 9.34307 24.009 10.253L16.6268 17.6351L24.009 25.0172C24.9189 25.9271 24.9189 27.4024 24.009 28.3124C23.0991 29.2223 21.6238 29.2223 20.7139 28.3124L11.6842 19.2827C11.2292 18.8277 11.0018 18.2313 11.0018 17.6351Z"
                                        fill="#9DACBD"
                                    />
                                </svg>
                            </button>
                            <div className="close-button-label">Volver</div>
                        </div>

                        <FormField
                            name="search"
                            type="search"
                            placeholder="Buscar usuario"
                            width="269px"
                            isLoading={isLoading}
                            showLabel={false}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoComplete="off"
                        />
                    </div>

                    <div className="inventory-page-title position-absolute top-50 start-50 translate-middle">
                        Cuentas por Cobrar
                    </div>

                    <button
                        type="button"
                        className="btn download-button"
                        disabled={isDownloading}
                        onClick={handleDownloadExcel}
                    >
                        Descarga detalle de movimientos
                    </button>
                </div>

                <DataTable
                    columns={columns}
                    data={isLoading ? [] : tableData}
                    isLoading={isLoading}
                    variant="normal"
                    pagination={{
                        count: pagination.count,
                        next: pagination.next,
                        previous: pagination.previous,
                        currentPage: pagination.currentPage,
                        onNext: handleNext,
                        onPrevious: handlePrevious,
                    }}
                    onSort={({ accessor, direction }) => setSortConfig({ accessor, direction })}
                />
            </div>
        </div>
    );
};

export default AccountsReceivableTable;
